import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Box, Button, Fade, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { CancelButton } from "../../../shared/CancelButton";
import { ReusableCustomQuestionGroup } from "../../../shared/interfaces/ReusableCustomQuestionGroup";
import { useCustomQuestionGroupsByCustomer } from "../../../data/CustomWorkflowQuestion/useCustomQuestionGroupsByCustomer";
import { CustomQuestionGroupCard } from "./CustomQuestionGroupCard";

interface Props {
  customerId: number;
  open: boolean;
  onClose: () => void;
  selectedGroups?: ReusableCustomQuestionGroup[];
  onApplySelection: (selectedQuestions: ReusableCustomQuestionGroup[]) => void;
}

export function ReusableQuestionGroupSelectionDialog(props: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { customerId, open, onClose } = props;

  const [amendedSelectedGroups, setAmendedSelectedGroups] = useState(props.selectedGroups || []);

  const customQuestionGroups = useCustomQuestionGroupsByCustomer(customerId);

  function handleSelectionChange(questionGroup: ReusableCustomQuestionGroup) {
    toggleSelected(questionGroup);
  }

  function handleApplySelectionChange() {
    props.onApplySelection(amendedSelectedGroups);
    onClose();
  }

  function toggleSelected(questionGroup: ReusableCustomQuestionGroup) {
    const groupIsSelected = amendedSelectedGroups?.some((selectedQuestion) => selectedQuestion.id === questionGroup.id);

    if (groupIsSelected)
      setAmendedSelectedGroups((prevSelectedQuestions) =>
        prevSelectedQuestions?.filter((q) => q.id !== questionGroup.id),
      );
    else setAmendedSelectedGroups((prevSelectedQuestions) => [...(prevSelectedQuestions || []), questionGroup]);
  }

  const header = <>{t("customQuestionLibrary.selectQuestions")}</>;

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1} marginTop={0}>
      <Button variant="contained" onClick={handleApplySelectionChange}>
        {!props.selectedGroups || props.selectedGroups.length === 0
          ? t("customQuestionLibrary.addSelectedGroups")
          : t("customQuestionLibrary.applySelection")}
      </Button>
      <CancelButton onClick={onClose} />
    </Stack>
  );

  if (customQuestionGroups.isLoading)
    return (
      <>
        <Fade
          in={customQuestionGroups.isLoading}
          style={{
            transitionDelay: customQuestionGroups.isLoading ? "800ms" : "0ms",
          }}
        >
          <Stack spacing={2} margin={2}>
            {[...Array(10).keys()].map((i) => (
              <Skeleton key={`skeleton-row-${i}`} variant="rectangular" height={80} />
            ))}
          </Stack>
        </Fade>
      </>
    );

  return (
    <Dialog
      header={header}
      visible={open}
      onHide={onClose}
      footer={footer}
      breakpoints={{ "1300px": "90vw" }}
      style={{ width: "50vw", height: "90vh" }}
    >
      <Stack>
        {customQuestionGroups.data && customQuestionGroups.data.length > 0 ? (
          <Paper
            variant="outlined"
            sx={{
              mt: 1,
              py: 2,
              px: 4,
              backgroundColor: theme.palette.darkBackground.main,
            }}
          >
            <Stack spacing={3}>
              {customQuestionGroups.data.map((questionGroup) => (
                <CustomQuestionGroupCard
                  key={`questionGroup-${questionGroup.id}`}
                  customerId={customerId}
                  customQuestionGroup={questionGroup}
                  displayMode="select"
                  isSelected={amendedSelectedGroups?.some(
                    (selectedQuestion) => selectedQuestion.id === questionGroup.id,
                  )}
                  onSelectionChange={handleSelectionChange}
                />
              ))}
            </Stack>
          </Paper>
        ) : (
          <Box display="flex" justifyContent="center" my={2}>
            <Typography variant="caption" noWrap>
              {t("customQuestions.customQuestionsEmpty")}
            </Typography>
          </Box>
        )}
      </Stack>
    </Dialog>
  );
}
